import { AnfrageDisplayModel, AnredeDisplayModel, KooperationspartnerDisplayModel, KooperationspartnerDisplayModelFormGroup, TitelDisplayModel } from '@/api/model-index';
import { AnfrageService, ProjektbeteiligterService } from '@/api/service-index';
import { Constants } from '@/constants';
import { AnfrageNavigationFormComponent, MoveDirection } from '@/core/components/anfrage-navigation-form/anfrage-navigation-form.component';
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from '@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { AnfrageTransformerService } from '@/core/services/anfrage-transformer.service';
import { ComponentService } from '@/core/services/component.service';
import { I18nService } from '@/core/services/i18nService';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { SectionService } from '@/core/services/section.service';
import { StammdatenService } from '@/core/services/stammdaten.service';
import { StateService } from '@/core/services/state.service';
import { TextBoxService } from '@/core/services/textbox.service';
import { UnfinishedHttpMethodsService } from '@/core/services/unfinished-http-methods.service';
import { deserializeHttpError } from '@/core/utils/deserialize-http-error';
import { GlobalNotificationHandler } from '@/core/utils/notifications/global-notification-handler';
import { UserFriendlyException } from '@/core/utils/notifications/user-friendly-exception.notification';
import { emailValidator } from '@/core/validators/customform.validators';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import { EditEvent, GridDataResult, RemoveEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kooperationspartner-process-step',
  templateUrl: './kooperationspartner-process-step.component.html',
  styleUrl: './kooperationspartner-process-step.component.scss',
  standalone: false
})
export class KooperationspartnerProcessStepComponent extends AnfrageNavigationFormComponent {
  public kooperationspartner: KooperationspartnerDisplayModel[] = [];
  public kooperationspartnerForm: KooperationspartnerDisplayModelFormGroup;
  public anfrage: AnfrageDisplayModel;
  public gridView: GridDataResult;
  public isFormOpen: boolean = false;
  public anredeItems: AnredeDisplayModel[] = [];
  public praefixTitelItems: TitelDisplayModel[] = [];

  protected languageSubscription: Subscription;

  public get formGroup(): FormGroup<any> {
    return this.kooperationspartnerForm;
  }

  public sort: SortDescriptor[] = [
    { field: "lastname", dir: "asc" }
  ];

  constructor(
    protected sectionService: SectionService,
    protected route: ActivatedRoute,
    protected loadingAnimationService: LoadingAnimationService,
    protected router: Router,
    protected stateService: StateService,
    protected projektbeteiligterService: ProjektbeteiligterService,
    protected portalContextService: PortalContextService,
    protected unfinishedHttpMethodsService: UnfinishedHttpMethodsService,
    protected languageService: I18nService,
    protected dialogService: DialogService,
    protected textBoxService: TextBoxService,
    protected componentService: ComponentService,
    protected anfrageService: AnfrageService,
    protected stammdatenService: StammdatenService,
    protected anfrageTransformerService: AnfrageTransformerService,
    protected notificationHandler: GlobalNotificationHandler
  ) {
    super(sectionService, router, route, portalContextService);
    this.kooperationspartnerForm = KooperationspartnerDisplayModelFormGroup.Instance;
    this.anfrage = this.route.snapshot.data.anfrage;
    this.kooperationspartner = this.route.snapshot.data.kooperationspartner;

    this.initGridView();
  }

  public getAnredeName(anredeId: string): string {
    let anrede = this.anredeItems.find(x => x.anredeId == anredeId);
    return anrede?.name ?? "";
  }

  public getPraefixName(praefixId: string): string {
    let title = this.praefixTitelItems.find(x => x.titelId == praefixId);
    return title?.name ?? "";
  }

  override async canDeactivate(): Promise<boolean> {
    if (!this.kooperationspartnerForm.valid && this.isFormOpen) {
      return this.openUnsavedChangesDialog();
    }
    return true;
  }

  get emailErrorMessage(): string {
    if (this.kooperationspartnerForm.EmailAddress?.errors?.email)
      return this.languageService.instant('contactinformation.emailAddress.invalid');

    return "";
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadAnreden();

    this.languageSubscription = this.languageService.languageChanged$.subscribe(() => {
      this.loadAnreden();
    });

    this.stammdatenService.GetTitel().subscribe(titles => {
      this.praefixTitelItems = titles.filter(titelItem => !titelItem.isPostfixTitel);
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.languageSubscription?.unsubscribe();
  }

  protected loadAnreden(): void {
    this.stammdatenService.GetAnreden().subscribe(anreden => this.anredeItems = this.languageService.filterAnredenKeepSpecificId(anreden, this.kooperationspartnerForm.AnredeId.value));
  }

  protected initGridView() {
    this.gridView = {
      data: this.kooperationspartner.sort((a, b) => a.lastname!.localeCompare(b.lastname!)),
      total: this.kooperationspartner.length,
    };
  }

  protected removeHandler({ dataItem }: RemoveEvent): void {
    let deleteDialogRef = this.dialogService.open({
      content: this.componentService.get(ConfirmationDialogComponent),
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      title: this.languageService.instant("kooperationspartner.dialog.titleDelete")
    });
    let dialogComponent: ConfirmationDialogComponent = deleteDialogRef.content.instance;

    dialogComponent.content = this.languageService.instant("kooperationspartner.dialog.contentDelete");

    deleteDialogRef.result.subscribe(result => this.onDeleteDialogClose(<ConfirmationDialogResult>result, dataItem));
  }

  protected onDeleteDialogClose(result: ConfirmationDialogResult, dataItem: KooperationspartnerDisplayModel) {
    if (result.action == ConfirmationDialogAction.Confirm) {
      const subscription = this.projektbeteiligterService.apiProjektbeteiligterDeleteKooperationspartnerIdAnfrageIdDelete({ id: dataItem.id!, anfrageId: this.portalContextService.CurrentAnfrage.id }).subscribe(() => {
        const index = this.kooperationspartner.findIndex(item => item.id === dataItem.id);
        if (index !== -1) {
          const removed = this.kooperationspartner.splice(index, 1);
          if (this.isFormOpen && removed[0].id === this.kooperationspartnerForm.value.id) {
            this.isFormOpen = false;
          }
          this.initGridView();
        }
      });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  public createHandler() {
    this.openForm(null);
  }

  public editHandler({ dataItem }: EditEvent) {
    this.openForm(dataItem);
  }

  public openForm(kooperationspartner: KooperationspartnerDisplayModel | null) {
    this.kooperationspartnerForm = KooperationspartnerDisplayModelFormGroup.Instance;
    this.kooperationspartnerForm.AnredeId.addValidators(Validators.required);
    this.kooperationspartnerForm.Firstname.addValidators(Validators.required);
    this.kooperationspartnerForm.Lastname.addValidators(Validators.required);
    this.kooperationspartnerForm.EmailAddress.addValidators([Validators.required, emailValidator]);

    if (kooperationspartner) {
      this.kooperationspartnerForm.patchValue(kooperationspartner);
    }

    this.isFormOpen = true;
  }

  public cancelForm() {
    this.isFormOpen = false;
  }

  public saveKooperationspartner(): void {
    const kooperationspartner: KooperationspartnerDisplayModel = this.kooperationspartnerForm.value;
    this.isFormOpen = false;

    let subscription = this.projektbeteiligterService.apiProjektbeteiligterUpdateKooperationspartnerAnfrageIdPatch({ anfrageId: this.anfrage.id, body: kooperationspartner }).subscribe((response) => {
      if (response && !kooperationspartner.id) {
        kooperationspartner.id = response;
        this.kooperationspartner.push(kooperationspartner);
      }
      else {
        const index = this.kooperationspartner.findIndex(item => item.id === kooperationspartner.id);
        if (index !== -1) {
          this.kooperationspartner[index] = kooperationspartner;
        }
      }


      this.initGridView();

      this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.anfrage.id }).subscribe(result => {
        this.stateService.ValidationErrors = result;
      })
    }, error => {
      error = deserializeHttpError(error)
      if (error instanceof HttpErrorResponse && error.error?.Errors[0]?.AdditionalProperties?.Code == "NOT_UNIQUE") {
        throw new UserFriendlyException(error);
      }
      else {
        throw error;
      }
    })

    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    this.unfinishedHttpMethodsService.addSubscription(subscription);
  }

  protected save(shouldSave: boolean, moveDirection: MoveDirection): void {
    // Nothing to save on that form 
    this.move(moveDirection);

    if (shouldSave && this.kooperationspartnerForm.valid && this.isFormOpen) {
      this.saveKooperationspartner();
    }
  }
}
